import { useState } from "react";
import { AlertCircle, Check, Send } from "lucide-react";
import { Reveal } from "./Reveal";
import { Sprig } from "./Botanical";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const WhatsAppLead = () => {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (status === "sending") return;
        setStatus("sending");
        setError("");
        try {
            const res = await fetch(`${API_BASE}/api/leads`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, phone }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
            setStatus("success");
        } catch (err) {
            setError(err.message);
            setStatus("error");
        }
    };

    return (
        <section className="relative bg-cream py-8 sm:py-12" data-testid="whatsapp-lead-section">
            <div className="mx-auto max-w-2xl px-5 sm:px-8">
                <Reveal>
                    <div
                        className="relative overflow-hidden rounded-2xl border border-blushline bg-white p-6 shadow-sm sm:p-8"
                        data-testid="lead-capture-card"
                    >
                        <Sprig className="pointer-events-none absolute -right-2 -top-2 w-10 rotate-[20deg] text-gold/20" />
                        {status === "success" ? (
                            <div className="flex items-start gap-3 py-2" data-testid="lead-success-message">
                                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blush">
                                    <Check className="h-4 w-4 text-gold" strokeWidth={3} />
                                </span>
                                <div>
                                    <p className="font-serif text-lg text-plum sm:text-xl">Done! Your guide link is on its way.</p>
                                    <p className="mt-1 text-sm leading-relaxed text-mutedtext">
                                        Check your WhatsApp &mdash; tap the link to get the 5-Day Husband Reset Guide for &#8377;49.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="font-sans text-[0.62rem] font-bold uppercase tracking-[0.26em] text-gold">
                                    Prefer WhatsApp?
                                </p>
                                <h3 className="mt-2 font-serif text-xl font-medium text-plum sm:text-2xl">
                                    Get the guide link on your phone
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-mutedtext sm:text-[0.95rem]">
                                    Drop your number and the instant-buy link for the 5-Day Husband Reset Guide (&#8377;49) will be
                                    sent to you.
                                </p>
                                <form
                                    onSubmit={submit}
                                    className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]"
                                    data-testid="lead-form"
                                >
                                    <input
                                        type="text"
                                        required
                                        minLength={2}
                                        maxLength={80}
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Your name"
                                        aria-label="Your name"
                                        className="rounded-full border border-blushline bg-ivory px-4 py-3 text-sm text-ink placeholder:text-mutedtext/60 focus:outline-none focus:ring-2 focus:ring-gold/40"
                                        data-testid="lead-name-input"
                                    />
                                    <input
                                        type="tel"
                                        required
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="WhatsApp number"
                                        aria-label="WhatsApp number"
                                        className="rounded-full border border-blushline bg-ivory px-4 py-3 text-sm text-ink placeholder:text-mutedtext/60 focus:outline-none focus:ring-2 focus:ring-gold/40"
                                        data-testid="lead-phone-input"
                                    />
                                    <button
                                        type="submit"
                                        disabled={status === "sending"}
                                        className="inline-flex items-center justify-center gap-2 rounded-full bg-burgundy px-6 py-3 font-sans text-[0.7rem] font-bold uppercase tracking-[0.14em] text-cream shadow-lg shadow-burgundy/20 transition-colors duration-300 hover:bg-plum disabled:opacity-60"
                                        data-testid="lead-submit-button"
                                    >
                                        <Send className="h-3.5 w-3.5" />
                                        {status === "sending" ? "Sending\u2026" : "Send Me The Link"}
                                    </button>
                                </form>
                                {status === "error" && (
                                    <p
                                        className="mt-3 flex items-center gap-2 text-sm text-burgundy"
                                        data-testid="lead-error-message"
                                    >
                                        <AlertCircle className="h-4 w-4" />
                                        {error}
                                    </p>
                                )}
                                <p className="mt-4 text-xs text-mutedtext/80">
                                    We&rsquo;ll only use your number to send the guide link. No spam, no calls.
                                </p>
                            </>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default WhatsAppLead;
